if (!customElements.get('rd-product-gallery')) {
  customElements.define(
    'rd-product-gallery',
    class RoomdenProductGallery extends HTMLElement {
      constructor() {
        super();
        this.thumbnails = Array.from(this.querySelectorAll('[data-media-thumb]'));
        this.slides = Array.from(this.querySelectorAll('[data-media-slide]'));
        this.stage = this.querySelector('[data-roomden-gallery-stage-slot]');
        this.rail = this.querySelector('[data-roomden-gallery-rail-slot]');
        this.stackMode = this.dataset.galleryMode === 'scroll_stack';
        this.stackMediaQuery = window.matchMedia('(min-width: 621px)');
        this.counter = this.querySelector('[data-gallery-index]');
        this.previousButton = this.querySelector('[data-gallery-prev]');
        this.nextButton = this.querySelector('[data-gallery-next]');
        this.zoomButton = this.querySelector('[data-gallery-zoom]');
        this.dialog = this.querySelector('[data-gallery-dialog]');
        this.dialogClose = this.querySelector('[data-gallery-dialog-close]');
        this.dialogStack = this.querySelector('[data-gallery-dialog-stack]');
        this.dialogThumbnails = Array.from(this.querySelectorAll('[data-gallery-dialog-thumb]'));
        this.dialogItems = Array.from(this.querySelectorAll('[data-gallery-dialog-item]'));
        this.dialogActiveIndex = 0;
        this.dialogTrigger = null;
        this.dialogScrollFrame = null;
        this.activeIndex = Math.max(
          0,
          this.slides.findIndex((slide) => slide.classList.contains('is-active'))
        );
        this.pointerStartX = null;
        this.didSwipe = false;
        this.unsubscribeVariantChange = null;
        this.stackScrollFrame = null;
        this.onStackScroll = () => {
          if (this.stackScrollFrame) return;
          this.stackScrollFrame = requestAnimationFrame(() => {
            this.stackScrollFrame = null;
            this.updateStackActiveFromViewport();
          });
        };
        this.onStackMediaQueryChange = () => this.refreshStackLayout();
        this.onDialogScroll = () => {
          if (this.dialogScrollFrame) return;
          this.dialogScrollFrame = requestAnimationFrame(() => {
            this.dialogScrollFrame = null;
            this.updateDialogActiveFromViewport();
          });
        };
        this.onDialogKeydown = (event) => this.handleDialogKeydown(event);
        this.onDialogClose = () => {
          const focusTarget = this.dialogTrigger?.isConnected ? this.dialogTrigger : this.zoomButton;
          focusTarget?.focus();
          this.dialogTrigger = null;
        };
      }

      connectedCallback() {
        if (!this.slides.length) return;

        this.thumbnails.forEach((thumbnail, index) => {
          thumbnail.addEventListener('click', () => this.show(index, { focusThumbnail: false }));
          thumbnail.addEventListener('keydown', (event) => this.onThumbnailKeydown(event, index));
        });

        this.previousButton?.addEventListener('click', () => this.show(this.activeIndex - 1));
        this.nextButton?.addEventListener('click', () => this.show(this.activeIndex + 1));
        this.stage?.addEventListener('click', (event) => this.onStageClick(event));
        this.stage?.addEventListener('keydown', (event) => this.onStageKeydown(event));
        this.stage?.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'mouse') {
            this.didSwipe = false;
            this.pointerStartX = event.clientX;
          }
        });
        this.stage?.addEventListener('pointerup', (event) => this.onPointerEnd(event));
        this.stage?.addEventListener('pointercancel', () => {
          this.pointerStartX = null;
          this.didSwipe = false;
        });
        this.zoomButton?.addEventListener('click', () => this.openZoom());
        this.dialogClose?.addEventListener('click', () => this.dialog?.close());
        this.dialogThumbnails.forEach((thumbnail, index) => {
          thumbnail.addEventListener('click', () => this.showDialogImage(index));
          thumbnail.addEventListener('keydown', (event) => this.onDialogThumbnailKeydown(event, index));
        });
        this.dialogStack?.addEventListener('scroll', this.onDialogScroll, { passive: true });
        this.dialog?.addEventListener('keydown', this.onDialogKeydown);
        this.dialog?.addEventListener('close', this.onDialogClose);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            const mediaId = data?.variant?.featured_media?.id;
            if (mediaId) this.showMediaById(mediaId);
          });
        }

        const initialMediaId = this.dataset.initialMediaId;
        const initialIndex = this.slides.findIndex((slide) => slide.dataset.mediaId === initialMediaId);
        this.activeIndex = initialIndex >= 0 ? initialIndex : this.activeIndex;
        const initialDialogIndex = this.dialogItems.findIndex(
          (item) => item.dataset.mediaId === this.slides[this.activeIndex]?.dataset.mediaId
        );
        this.setDialogActive(initialDialogIndex >= 0 ? initialDialogIndex : 0);
        if (this.stackMode) {
          this.stackMediaQuery.addEventListener('change', this.onStackMediaQueryChange);
          this.refreshStackLayout();
        } else {
          this.show(this.activeIndex, { announce: false });
        }
      }

      disconnectedCallback() {
        this.unsubscribeVariantChange?.();
        this.stackMediaQuery.removeEventListener('change', this.onStackMediaQueryChange);
        window.removeEventListener('scroll', this.onStackScroll);
        window.removeEventListener('resize', this.onStackScroll);
        if (this.stackScrollFrame) cancelAnimationFrame(this.stackScrollFrame);
        this.dialogStack?.removeEventListener('scroll', this.onDialogScroll);
        this.dialog?.removeEventListener('keydown', this.onDialogKeydown);
        this.dialog?.removeEventListener('close', this.onDialogClose);
        if (this.dialogScrollFrame) cancelAnimationFrame(this.dialogScrollFrame);
      }

      normalizeIndex(index) {
        return (index + this.slides.length) % this.slides.length;
      }

      show(index, options = {}) {
        const nextIndex = this.normalizeIndex(index);
        const nextSlide = this.slides[nextIndex];
        if (!nextSlide) return;

        if (this.isStackLayout()) {
          this.setStackActive(nextIndex);
          nextSlide.scrollIntoView({
            behavior: options.instant ? 'auto' : 'smooth',
            block: 'start',
          });
          if (options.focusThumbnail) this.thumbnails[nextIndex]?.focus();
          return;
        }

        if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();

        this.slides.forEach((slide, slideIndex) => {
          const isActive = slideIndex === nextIndex;
          slide.hidden = !isActive;
          slide.classList.toggle('is-active', isActive);
        });

        this.thumbnails.forEach((thumbnail, thumbnailIndex) => {
          const isActive = thumbnailIndex === nextIndex;
          thumbnail.setAttribute('aria-selected', String(isActive));
          thumbnail.tabIndex = isActive ? 0 : -1;
        });

        this.activeIndex = nextIndex;
        if (this.counter) this.counter.textContent = String(nextIndex + 1);
        if (this.stage) this.stage.setAttribute('aria-labelledby', this.thumbnails[nextIndex]?.id || '');
        if (this.zoomButton?.dataset.labelTemplate) {
          this.zoomButton.setAttribute('aria-label', this.zoomButton.dataset.labelTemplate.replace('[index]', nextIndex + 1));
        }

        const deferredMedia = nextSlide.querySelector('deferred-media, product-model');
        if (deferredMedia?.loadContent) deferredMedia.loadContent(false);

        const isImage = nextSlide.dataset.mediaType === 'image';
        if (this.zoomButton) {
          this.zoomButton.hidden = !isImage;
        }

        this.thumbnails[nextIndex]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        if (options.focusThumbnail) this.thumbnails[nextIndex]?.focus();
      }

      isStackLayout() {
        return this.stackMode && this.stackMediaQuery.matches;
      }

      refreshStackLayout() {
        window.removeEventListener('scroll', this.onStackScroll);
        window.removeEventListener('resize', this.onStackScroll);

        if (!this.isStackLayout()) {
          this.show(this.activeIndex, { announce: false });
          return;
        }

        this.slides.forEach((slide) => {
          slide.hidden = false;
        });
        this.setStackActive(this.activeIndex);
        window.addEventListener('scroll', this.onStackScroll, { passive: true });
        window.addEventListener('resize', this.onStackScroll, { passive: true });
        requestAnimationFrame(() => this.updateStackActiveFromViewport());
      }

      setStackActive(index) {
        const nextIndex = this.normalizeIndex(index);
        this.thumbnails.forEach((thumbnail, thumbnailIndex) => {
          const isActive = thumbnailIndex === nextIndex;
          thumbnail.setAttribute('aria-selected', String(isActive));
          thumbnail.tabIndex = isActive ? 0 : -1;
        });
        this.activeIndex = nextIndex;
        if (this.counter) this.counter.textContent = String(nextIndex + 1);
        this.scrollStackThumbnailIntoView(this.thumbnails[nextIndex]);
      }

      scrollStackThumbnailIntoView(thumbnail) {
        if (!thumbnail || !this.rail) return;

        const railRect = this.rail.getBoundingClientRect();
        const thumbnailRect = thumbnail.getBoundingClientRect();

        if (thumbnailRect.top < railRect.top) {
          this.rail.scrollTop -= railRect.top - thumbnailRect.top;
        } else if (thumbnailRect.bottom > railRect.bottom) {
          this.rail.scrollTop += thumbnailRect.bottom - railRect.bottom;
        }
      }

      updateStackActiveFromViewport() {
        if (!this.isStackLayout()) return;
        const anchor = Math.min(window.innerHeight * 0.32, 28 * 10);
        let nextIndex = this.activeIndex;
        let nearestDistance = Number.POSITIVE_INFINITY;

        this.slides.forEach((slide, index) => {
          const rect = slide.getBoundingClientRect();
          if (rect.bottom <= 0 || rect.top >= window.innerHeight) return;
          const distance = Math.abs(rect.top - anchor);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nextIndex = index;
          }
        });

        this.setStackActive(nextIndex);
      }

      showMediaById(mediaId) {
        const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
        if (index >= 0) this.show(index);
      }

      onThumbnailKeydown(event, index) {
        const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
        if (!keys.includes(event.key)) return;
        event.preventDefault();

        if (event.key === 'Home') return this.show(0, { focusThumbnail: true });
        if (event.key === 'End') return this.show(this.slides.length - 1, { focusThumbnail: true });

        const backwards = event.key === 'ArrowLeft' || event.key === 'ArrowUp';
        this.show(index + (backwards ? -1 : 1), { focusThumbnail: true });
      }

      onStageKeydown(event) {
        if (event.key === 'Enter' || event.key === ' ') {
          const activeSlide = this.slides[this.activeIndex];
          if (activeSlide?.dataset.mediaType !== 'image') return;
          event.preventDefault();
          this.openZoom();
          return;
        }

        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.show(this.activeIndex + (event.key === 'ArrowLeft' ? -1 : 1));
      }

      onStageClick(event) {
        if (this.didSwipe) {
          this.didSwipe = false;
          return;
        }

        const clickedSlide = event.target.closest('[data-media-slide]');
        if (this.isStackLayout() && clickedSlide) {
          const clickedIndex = this.slides.indexOf(clickedSlide);
          if (clickedIndex >= 0) this.setStackActive(clickedIndex);
        }
        const activeSlide = this.slides[this.activeIndex];
        const image = event.target.closest('img');
        if (!image || !activeSlide?.contains(image) || activeSlide.dataset.mediaType !== 'image') return;
        this.openZoom();
      }

      onPointerEnd(event) {
        if (this.pointerStartX === null) return;
        const distance = event.clientX - this.pointerStartX;
        this.pointerStartX = null;
        if (Math.abs(distance) < 44) return;
        this.didSwipe = true;
        this.show(this.activeIndex + (distance < 0 ? 1 : -1));
      }

      setDialogActive(index) {
        if (!this.dialogItems.length) return;
        const nextIndex = Math.min(Math.max(index, 0), this.dialogItems.length - 1);

        this.dialogThumbnails.forEach((thumbnail, thumbnailIndex) => {
          const isActive = thumbnailIndex === nextIndex;
          thumbnail.setAttribute('aria-current', String(isActive));
          thumbnail.tabIndex = isActive ? 0 : -1;
        });

        this.dialogActiveIndex = nextIndex;
        this.dialogThumbnails[nextIndex]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }

      showDialogImage(index, options = {}) {
        if (!this.dialogStack || !this.dialogItems.length) return;
        const nextIndex = Math.min(Math.max(index, 0), this.dialogItems.length - 1);
        const nextItem = this.dialogItems[nextIndex];
        const stackRect = this.dialogStack.getBoundingClientRect();
        const itemRect = nextItem.getBoundingClientRect();
        const nextTop = this.dialogStack.scrollTop + itemRect.top - stackRect.top;

        this.setDialogActive(nextIndex);
        if (options.instant) {
          this.dialogStack.style.scrollBehavior = 'auto';
          this.dialogStack.scrollTop = nextTop;
          requestAnimationFrame(() => this.dialogStack?.style.removeProperty('scroll-behavior'));
          return;
        }

        this.dialogStack.scrollTo({
          top: nextTop,
          behavior: 'smooth',
        });
        if (options.focusThumbnail) this.dialogThumbnails[nextIndex]?.focus();
      }

      updateDialogActiveFromViewport() {
        if (!this.dialog?.open || !this.dialogStack || !this.dialogItems.length) return;
        const stackRect = this.dialogStack.getBoundingClientRect();
        const anchor = stackRect.top + Math.min(stackRect.height * 0.24, 180);
        let nextIndex = this.dialogActiveIndex;
        let nearestDistance = Number.POSITIVE_INFINITY;

        this.dialogItems.forEach((item, index) => {
          const rect = item.getBoundingClientRect();
          if (rect.bottom <= stackRect.top || rect.top >= stackRect.bottom) return;
          const distance = Math.abs(rect.top - anchor);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nextIndex = index;
          }
        });

        this.setDialogActive(nextIndex);
      }

      onDialogThumbnailKeydown(event, index) {
        const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
        if (!keys.includes(event.key)) return;
        event.preventDefault();
        event.stopPropagation();

        if (event.key === 'Home') return this.showDialogImage(0, { focusThumbnail: true });
        if (event.key === 'End') {
          return this.showDialogImage(this.dialogItems.length - 1, { focusThumbnail: true });
        }

        const backwards = event.key === 'ArrowLeft' || event.key === 'ArrowUp';
        this.showDialogImage(index + (backwards ? -1 : 1), { focusThumbnail: true });
      }

      handleDialogKeydown(event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.dialog?.close();
          return;
        }

        if (event.metaKey || event.ctrlKey || event.altKey) return;
        const directionKeys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'];
        if (!directionKeys.includes(event.key)) return;
        event.preventDefault();

        if (event.key === 'Home') return this.showDialogImage(0);
        if (event.key === 'End') return this.showDialogImage(this.dialogItems.length - 1);

        const backwards = event.key === 'ArrowUp' || event.key === 'PageUp';
        this.showDialogImage(this.dialogActiveIndex + (backwards ? -1 : 1));
      }

      loadDialogImages(activeIndex) {
        this.dialogItems.forEach((item, index) => {
          const image = item.querySelector('img');
          if (!image) return;
          image.loading = 'eager';
          image.fetchPriority = Math.abs(index - activeIndex) <= 1 ? 'high' : 'auto';
        });
      }

      openZoom() {
        const activeSlide = this.slides[this.activeIndex];
        if (activeSlide?.dataset.mediaType !== 'image' || !this.dialog || !this.dialogStack) return;
        const dialogIndex = this.dialogItems.findIndex(
          (item) => item.dataset.mediaId === activeSlide.dataset.mediaId
        );
        if (dialogIndex < 0) return;

        this.loadDialogImages(dialogIndex);
        this.dialogTrigger = document.activeElement;
        this.dialog.showModal();
        requestAnimationFrame(() => {
          this.showDialogImage(dialogIndex, { instant: true });
          this.dialogClose?.focus();
        });
      }
    }
  );
}

if (!customElements.get('rd-product-purchase')) {
  customElements.define(
    'rd-product-purchase',
    class RoomdenProductPurchase extends HTMLElement {
      constructor() {
        super();
        this.unsubscribeVariantChange = null;
      }

      connectedCallback() {
        this.button = this.querySelector('[id^="ProductSubmitButton-"]');
        this.buttonText = this.querySelector('[data-add-button-text]');
        this.variantInput = this.querySelector('input[name="id"]');
        if (!this.button || !this.buttonText || !this.variantInput) {
          if (!this.initializationObserver) {
            this.initializationObserver = new MutationObserver(() => {
              if (!this.querySelector('[id^="ProductSubmitButton-"]') || !this.querySelector('input[name="id"]')) return;
              this.initializationObserver.disconnect();
              this.initializationObserver = null;
              this.connectedCallback();
            });
            this.initializationObserver.observe(this, { childList: true, subtree: true });
          }
          if (!this.initializationTimer && (this.initializationAttempts || 0) < 3) {
            this.initializationAttempts = (this.initializationAttempts || 0) + 1;
            this.initializationTimer = window.setTimeout(() => {
              this.initializationTimer = null;
              if (this.isConnected && !this.initialized) this.connectedCallback();
            }, 0);
          }
          return;
        }
        if (this.initialized) return;
        this.initialized = true;
        this.initializationAttempts = 0;
        this.form = this.querySelector('form[data-type="add-to-cart-form"]');
        this.onVariantInputChange = () => this.updateUrl(this.variantInput?.value || '');
        this.variantInput?.addEventListener('change', this.onVariantInputChange);
        this.onFormSubmit = () => this.syncSelectedVariantInput();
        this.form?.addEventListener('submit', this.onFormSubmit, true);
        this.syncSelectedVariantInput();

        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
        this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          this.update(data?.variant, data?.html, data?.sectionId);
        });
      }

      disconnectedCallback() {
        this.initializationObserver?.disconnect();
        this.initializationObserver = null;
        window.clearTimeout(this.initializationTimer);
        this.initializationTimer = null;
        this.variantInput?.removeEventListener('change', this.onVariantInputChange);
        this.form?.removeEventListener('submit', this.onFormSubmit, true);
        this.unsubscribeVariantChange?.();
        this.unsubscribeVariantChange = null;
        this.initialized = false;
      }

      setVariantInput(variantId) {
        if (!this.variantInput) return;
        const value = variantId ? String(variantId) : '';
        this.variantInput.value = value;
        this.variantInput.defaultValue = value;
      }

      syncSelectedVariantInput() {
        const selectedVariant = this.closest('product-info')?.querySelector('variant-selects [data-selected-variant]');
        if (!selectedVariant?.textContent) return;

        try {
          this.setVariantInput(JSON.parse(selectedVariant.textContent).id);
        } catch {
          // The server-rendered value remains the source of truth if this response is incomplete.
        }
      }

      updateUrl(variantId) {
        const url = new URL(window.location.href);
        if (variantId) {
          url.searchParams.set('variant', variantId);
        } else {
          url.searchParams.delete('variant');
        }
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      }

      update(variant, html, sectionId) {
        if (!this.button || !this.buttonText) return;

        if (!variant) {
          this.setVariantInput();
          this.button.disabled = true;
          this.buttonText.textContent = this.dataset.unavailableLabel;
          return;
        }

        if (!variant.available) {
          this.setVariantInput(variant.id);
          this.updateUrl(variant.id);
          this.button.disabled = true;
          this.buttonText.textContent = this.dataset.soldOutLabel;
          return;
        }

        this.setVariantInput(variant.id);
        this.updateUrl(variant.id);
        this.button.disabled = false;
        this.buttonText.textContent = this.dataset.addLabel;
      }
    }
  );
}

if (!customElements.get('rd-product-accordions')) {
  customElements.define(
    'rd-product-accordions',
    class RoomdenProductAccordions extends HTMLElement {
      connectedCallback() {
        const sizeGuide = this.querySelector('[data-size-guide-accordion]');
        if (!sizeGuide?.id) return;

        this.openSizeGuide = (event) => {
          const link = event.target.closest(`a[href="#${sizeGuide.id}"]`);
          if (!link || !this.productInfo?.contains(link)) return;
          event.preventDefault();
          sizeGuide.open = true;
          sizeGuide.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'start',
          });
          sizeGuide.querySelector('summary')?.focus({ preventScroll: true });
        };
        this.productInfo = this.closest('product-info');
        this.productInfo?.addEventListener('click', this.openSizeGuide);
      }

      disconnectedCallback() {
        this.productInfo?.removeEventListener('click', this.openSizeGuide);
      }
    }
  );
}

if (!customElements.get('rd-size-guide-scroller')) {
  customElements.define(
    'rd-size-guide-scroller',
    class RoomdenSizeGuideScroller extends HTMLElement {
      connectedCallback() {
        this.scroller = this.querySelector('[data-size-guide-scroll]');
        this.progress = this.querySelector('[data-size-guide-progress]');
        if (!this.scroller || !this.progress) return;

        this.update = this.update.bind(this);
        this.scroller.addEventListener('scroll', this.update, { passive: true });
        this.resizeObserver = new ResizeObserver(this.update);
        this.resizeObserver.observe(this.scroller);
        this.update();
      }

      disconnectedCallback() {
        this.scroller?.removeEventListener('scroll', this.update);
        this.resizeObserver?.disconnect();
      }

      update() {
        const { clientWidth, scrollWidth, scrollLeft } = this.scroller;
        const visibleRatio = scrollWidth > 0 ? Math.min(1, clientWidth / scrollWidth) : 1;
        const travel = Math.max(0, scrollWidth - clientWidth);
        const progress = travel > 0 ? Math.abs(scrollLeft) / travel : 0;
        const width = visibleRatio * 100;
        const offset = progress * (100 - width);
        this.progress.style.setProperty('--rd-size-progress-width', `${width}%`);
        this.progress.style.setProperty('--rd-size-progress-offset', `${offset}%`);
        this.progress.parentElement.hidden = visibleRatio >= 1;
      }
    }
  );
}

if (!customElements.get('rd-complementary-rail')) {
  customElements.define(
    'rd-complementary-rail',
    class RoomdenComplementaryRail extends HTMLElement {
      connectedCallback() {
        this.rail = this.querySelector('[data-complementary-rail]');
        this.scrollbar = this.querySelector('[data-complementary-scrollbar]');
        if (!this.rail || !this.scrollbar || this.initialized) return;

        this.initialized = true;
        this.scrollFrame = null;
        this.onScroll = () => {
          if (this.scrollFrame) return;
          this.scrollFrame = requestAnimationFrame(() => {
            this.scrollFrame = null;
            this.updateScrollbar();
          });
        };
        this.onScrollbarInput = () => {
          const maximumScroll = Math.max(0, this.rail.scrollWidth - this.rail.clientWidth);
          this.rail.scrollLeft = maximumScroll * (Number(this.scrollbar.value) / 100);
        };

        this.rail.addEventListener('scroll', this.onScroll, { passive: true });
        this.scrollbar.addEventListener('input', this.onScrollbarInput);
        this.resizeObserver = new ResizeObserver(() => this.updateScrollbar());
        this.resizeObserver.observe(this.rail);
        this.updateScrollbar();
      }

      disconnectedCallback() {
        this.rail?.removeEventListener('scroll', this.onScroll);
        this.scrollbar?.removeEventListener('input', this.onScrollbarInput);
        this.resizeObserver?.disconnect();
        if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
        this.initialized = false;
      }

      updateScrollbar() {
        const maximumScroll = Math.max(0, this.rail.scrollWidth - this.rail.clientWidth);
        const visibleRatio = this.rail.scrollWidth ? this.rail.clientWidth / this.rail.scrollWidth : 1;
        const thumbWidth = Math.max(18, Math.min(100, visibleRatio * 100));

        this.scrollbar.value = maximumScroll ? String((this.rail.scrollLeft / maximumScroll) * 100) : '0';
        this.scrollbar.disabled = maximumScroll <= 1;
        this.scrollbar.style.setProperty('--rd-complementary-thumb-width', `${thumbWidth}%`);
        this.scrollbar.closest('.rd-product-v1__complementary-scrollbar')?.toggleAttribute('hidden', maximumScroll <= 1);
      }
    }
  );
}

if (!customElements.get('rd-mobile-atc')) {
  customElements.define(
    'rd-mobile-atc',
    class RoomdenMobileAtc extends HTMLElement {
      connectedCallback() {
        this.mainButton = document.getElementById(this.dataset.mainButtonId);
        this.button = this.querySelector('[data-mobile-add]');
        this.price = this.querySelector('[data-mobile-price]');
        this.options = this.querySelector('[data-mobile-options]');
        if (!this.mainButton || !this.button || !this.price || !this.options) {
          if (!this.initializationObserver) {
            this.initializationObserver = new MutationObserver(() => {
              if (!this.querySelector('[data-mobile-add]') || !this.querySelector('[data-mobile-price]')) return;
              this.initializationObserver.disconnect();
              this.initializationObserver = null;
              this.connectedCallback();
            });
            this.initializationObserver.observe(this, { childList: true, subtree: true });
          }
          if (!this.initializationTimer && (this.initializationAttempts || 0) < 3) {
            this.initializationAttempts = (this.initializationAttempts || 0) + 1;
            this.initializationTimer = window.setTimeout(() => {
              this.initializationTimer = null;
              if (this.isConnected && !this.initialized) this.connectedCallback();
            }, 0);
          }
          return;
        }
        if (this.initialized) return;
        this.initialized = true;
        this.initializationAttempts = 0;

        this.onMobileButtonClick = () => this.mainButton.click();
        this.button.addEventListener('click', this.onMobileButtonClick);
        this.syncMainButtonState = () => {
          const isBusy =
            this.mainButton.classList.contains('loading') || this.mainButton.getAttribute('aria-disabled') === 'true';
          this.button.disabled = this.mainButton.disabled || isBusy;
          this.button.toggleAttribute('aria-busy', isBusy);

          if (!isBusy && this.mainButton.disabled) {
            const mainText = this.mainButton.textContent.trim();
            this.button.textContent = mainText.includes(this.dataset.unavailableLabel)
              ? this.dataset.unavailableLabel
              : this.dataset.soldOutLabel;
          }
        };
        this.mainButtonObserver = new MutationObserver(this.syncMainButtonState);
        this.mainButtonObserver.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ['aria-disabled', 'class', 'disabled'],
          childList: true,
          subtree: true,
        });
        this.syncMainButtonState();
        this.mainButtonHasPassed = false;
        this.blockingElementVisibility = new Map();
        this.updateVisibility = () => {
          const isBlocked = Array.from(this.blockingElementVisibility.values()).some(Boolean);
          this.classList.toggle('is-visible', this.mainButtonHasPassed && !isBlocked);
        };
        this.observer = new IntersectionObserver(([entry]) => {
          this.mainButtonHasPassed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.updateVisibility();
        });
        this.observer.observe(this.mainButton);
        this.blockingObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => this.blockingElementVisibility.set(entry.target, entry.isIntersecting));
          this.updateVisibility();
        });
        [document.querySelector('footer')]
          .filter(Boolean)
          .forEach((element) => {
            this.blockingElementVisibility.set(element, false);
            this.blockingObserver.observe(element);
          });

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            this.update(data?.variant, data?.html, data?.sectionId);
          });
          this.unsubscribeOptionChange = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({ data }) => {
            if (data?.target?.closest('product-info') !== this.closest('product-info')) return;
            this.updateSelectedOptions();
          });
        }
      }

      disconnectedCallback() {
        this.initializationObserver?.disconnect();
        this.initializationObserver = null;
        window.clearTimeout(this.initializationTimer);
        this.initializationTimer = null;
        this.button?.removeEventListener('click', this.onMobileButtonClick);
        this.observer?.disconnect();
        this.blockingObserver?.disconnect();
        this.mainButtonObserver?.disconnect();
        this.unsubscribeVariantChange?.();
        this.unsubscribeOptionChange?.();
        this.unsubscribeVariantChange = null;
        this.unsubscribeOptionChange = null;
        this.initialized = false;
      }

      updateSelectedOptions() {
        if (!this.options) return;
        const values = Array.from(
          this.closest('product-info')?.querySelectorAll('variant-selects fieldset input:checked') || []
        ).map((input) => input.value);
        this.options.textContent = values.join(' / ');
      }

      update(variant, html, sectionId) {
        if (!variant) {
          this.button.disabled = true;
          this.button.textContent = this.dataset.unavailableLabel;
          return;
        }

        this.button.disabled = !variant.available;
        this.button.textContent = variant.available ? this.dataset.addLabel : this.dataset.soldOutLabel;
        const price = html?.querySelector(`#price-${sectionId} .rd-product-v1__price-amounts strong`)?.textContent?.trim();
        if (price) this.price.textContent = price;
        if (this.options) this.options.textContent = variant.options?.join(' / ') || '';
      }
    }
  );
}

// Normalize legacy prose structure without changing stored/shared content.
(() => {
  function normalize(root = document) {
    root.querySelectorAll('.rd-product-v1__accordion-content.rte').forEach(el => {
      if (el.dataset.proseNormalized) return;
      el.dataset.proseNormalized = 'true';
      el.classList.add('rd-accordion-prose');
      el.querySelectorAll('br').forEach(br => {
        let next = br.nextSibling;
        while (next && next.nodeType === Node.TEXT_NODE && !next.textContent.trim()) next = next.nextSibling;
        if (next?.nodeName === 'BR') br.remove();
      });
      el.querySelectorAll('p').forEach(p => {
        if (p.children.length === 1 && p.firstElementChild.tagName === 'STRONG' && p.textContent.trim() === p.firstElementChild.textContent.trim()) {
          const heading = document.createElement('h3');
          heading.textContent = p.textContent;
          p.replaceWith(heading);
        }
      });
      if (el.closest('[id^="RoomdenSetIncludes-"]')) el.querySelectorAll('p').forEach(p => {
        const first = p.firstChild;
        if (first?.nodeType !== Node.TEXT_NODE) return;
        const match = first.textContent.match(/^([^:]+:)(\s*)/);
        if (!match) return;
        const label = document.createElement('strong'); label.textContent = match[1];
        first.textContent = first.textContent.slice(match[1].length);
        p.insertBefore(label, first);
      });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => normalize());
  else normalize();
  document.addEventListener('shopify:section:load', event => normalize(event.target));
})();
